import { maxBy } from 'lodash';

import { StudyWorkflowModules, StudyWorkflowStepApproval } from 'interfaces/study';

import {
  getConfiguredMultiplexWorkflowSteps,
  getRnaProbeWorkflowSteps,
  multiplexCaseImageAnalysisDescription,
} from './multiplexWorkflowSteps';
import { StudyWorkflowStepDefinition } from './studyWorkflowStepTypes';

export type { StudyWorkflowStepCategory, StudyWorkflowStepDefinition } from './studyWorkflowStepTypes';

export const getLatestWorkflowStepApproval = (
  approvals: StudyWorkflowStepApproval[] | undefined,
  stepKey: string
): StudyWorkflowStepApproval | undefined =>
  maxBy(
    (approvals || []).filter((approval) => approval.stepKey === stepKey),
    'approvedAt'
  );

export const getWorkflowApproverName = (
  approval: StudyWorkflowStepApproval,
  approverNames?: Record<string, string> | null
): string => approval.approvedByName || approverNames?.[approval.approvedBy] || 'unknown user';

// A step is currently approved only when its most recent entry's action is 'approved'; a later
// 'revoked' entry means the step needs to be approved again, without erasing prior history.
export const isWorkflowStepApproved = (approvals: StudyWorkflowStepApproval[] | undefined, stepKey: string): boolean =>
  getLatestWorkflowStepApproval(approvals, stepKey)?.action === 'approved';

// Modality-agnostic study steps only. Do not add multiplex-only steps, labels, or wording here;
// put those in multiplexWorkflowSteps.ts and splice them in explicitly below.
const studyWorkflowSteps: StudyWorkflowStepDefinition[] = [
  {
    id: 'dataset-preparation',
    label: 'Dataset preparation',
    description: 'Upload study images and prepare them for research use.',
    category: 'study',
  },
  {
    id: 'case-organization',
    label: 'Case assignment',
    description: 'Assign study slides to the research cases used throughout the study.',
    category: 'study',
  },
  {
    id: 'case-image-analysis',
    label: 'Case initial image analysis',
    description:
      'Analyze each case image to extract tissue and cell data layers using the workflow for this image modality.',
    category: 'study',
  },
  {
    id: 'review-and-publication',
    label: 'View and export results',
    description: 'Review study results and export features or cell tables.',
    category: 'study',
  },
];

const applyModalitySpecificStepCopy = (
  steps: StudyWorkflowStepDefinition[],
  isMultiplexStudy: boolean
): StudyWorkflowStepDefinition[] =>
  steps.map((step) =>
    step.id === 'case-image-analysis' && isMultiplexStudy
      ? { ...step, description: multiplexCaseImageAnalysisDescription }
      : step
  );

export const getStudyWorkflowSteps = ({
  isMultiplexStudy,
  modules,
  previewConfiguredModules = false,
}: {
  isMultiplexStudy: boolean;
  modules?: StudyWorkflowModules;
  previewConfiguredModules?: boolean;
}) => {
  const resolvedStudySteps = applyModalitySpecificStepCopy(studyWorkflowSteps, isMultiplexStudy);
  const configuredSteps =
    isMultiplexStudy || previewConfiguredModules
      ? getConfiguredMultiplexWorkflowSteps(modules)
      : getRnaProbeWorkflowSteps(modules);

  if (!configuredSteps.length) {
    return resolvedStudySteps;
  }

  return [...resolvedStudySteps.slice(0, 3), ...configuredSteps, ...resolvedStudySteps.slice(3)];
};
